import { routingSwitchingWanFlags } from '@ipfabric/utils/schema/siteSeparation/routingSwitchingSiteSeparationRule';
import type { RoutingSwitchingWanFlag } from '@ipfabric/utils/schema/siteSeparation/routingSwitchingSiteSeparationRule';
import type { SiteSeparationAttributeTransformation } from '@ipfabric/utils/schema/siteSeparation/siteSeparationAttributeTransformation';
import type { SiteSeparationRule } from '@ipfabric/utils/schema/siteSeparation/siteSeparationRule';
import { CloudVendors } from '@ipfabric/utils/schema/siteSeparation/slugSiteSeparationRule';
import type { CloudVendor } from '@ipfabric/utils/schema/siteSeparation/slugSiteSeparationRule';
import type { Control } from 'react-hook-form';

export type SiteSeparationRuleType = SiteSeparationRule['type'];

export type RuleEditorValues = {
  type: SiteSeparationRuleType;
  note: string;
  regex: string;
  siteName: string | null;
  transformation: SiteSeparationAttributeTransformation;
  applyToCloudInstances: boolean;
  applyToAllVendors: boolean;
  minimumSiteSize: string;
  firewall: boolean;
  wan: RoutingSwitchingWanFlag[];
  enabledVendors: CloudVendor[];
  tagNames: string[];
};

export type RuleEditorContentProps = {
  control: Control<RuleEditorValues>;
};

type RuleEditorOption<TValue extends string> = {
  label: string;
  value: TValue;
};

export const TRANSFORMATION_OPTIONS: RuleEditorOption<SiteSeparationAttributeTransformation>[] =
  [
    { label: 'Upper case', value: 'uppercase' },
    { label: 'Lower case', value: 'lowercase' },
    { label: 'None', value: 'none' },
  ];

export const WAN_OPTIONS = routingSwitchingWanFlags.map((value) => ({
  label: value,
  value,
}));

export const VENDOR_OPTIONS = CloudVendors.map((value) => ({
  label: value.toUpperCase(),
  value,
}));

export const REGEX_TOOLTIP_TEXT =
  'You can use a capturing group (parentheses) to generate site name.';

export const CLOUD_RESOURCE_ID_TRANSFORMATION_TOOLTIP_TEXT =
  'Transform Cloud Resource ID before matching the regular expression and generating site name. For example, expression "(X.*)_.*" with transform "UPPER CASE" will match ID "xa_11" into site "XA", while with "lower case" or "none" it won\'t match that ID at all.';

export const CLOUD_RESOURCE_TAG_TRANSFORMATION_TOOLTIP_TEXT =
  'Transform the tag value before it becomes the site name. For example, tag value "prod" with transform "UPPER CASE" becomes site "PROD".';

export const CLOUD_RESOURCE_TAG_ORDER_DESCRIPTION =
  'Tags are evaluated in order - only the first match will be used as the site attribute';

export const CLOUD_RESOURCE_TAG_SELECTED_TAGS_DESCRIPTION =
  'Site name will be generated from a value of chosen tag(s). Those values are found during network discovery. If a resource carries more than one selected tag, the first one listed here wins.';

export const EXCLUDED_WAN_INTERFACES_TOOLTIP_TEXT =
  'For networks that have direct routing connectivity between sites, such as DMVPN or Leased Lines (usually over Serial or MFR interfaces), an option to separate the site using tunnel and/or serial interface should be selected.';

export const FIREWALL_AT_SITE_TOOLTIP_TEXT =
  'For situations where inaccessible routed firewall is used at the site (i.e. device under different management team), an option Firewall at site can be turned on, so the infrastructure before and behind firewall is not separated into two different sites.';

export const ROUTING_SWITCHING_TRANSFORMATION_TOOLTIP_TEXT =
  'The Routing & Switching rule will split devices into groups (sites) where the site name is determined based on the "Longest common string" algorithm. For this algorithm it is recommended to set all characters in hostname to lower or upper case, the resulted site name will be affected too.';
